/**
 * @file `<ChapterProvider/>` + chapter-state hooks (#629).
 * @module @labcraft/course-kit/runtime/context
 *
 * Owns the reader's progress through a chapter's objectives. Completion
 * is deterministic over the command-event stream: the provider
 * subscribes to a {@link CommandSource} (lab forwards the hypervisor's
 * `GateSub` command events as terminal `command` frames), runs
 * {@link scanObjective} against the active objective per event, and
 * dispatches `objective.completed` / `objective.wrongTurn`.
 *
 * There is **no Run button** — per the #927 lab-UX rethink, "the typing
 * is the doing": the learner types commands in the terminal themselves
 * and OSC 133 detection advances them (this is the whole reason #156
 * exists). `manual` objectives are the only ones completed by an
 * explicit click, via {@link useChapterDispatch}.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type Dispatch,
  type JSX,
  type ReactNode,
} from 'react'
import type { ChapterSpec, Objective } from '../types.ts'
import {
  chapterPhase,
  chapterReducer,
  initialState,
  type CapstoneState,
  type ChapterAction,
  type ChapterPhase,
  type ChapterState,
  type MentorDockDisposition,
  type MentorPending,
  type TranscriptEntry,
} from './reducer.ts'
import {
  scanObjective,
  scanObjectiveCheck,
  type CommandEvent,
} from './scanner.ts'
import { StateGoalScheduler, type ProbeSource } from './state_scheduler.ts'
import type { Unsubscribe } from './terminal.ts'

/**
 * How long (ms) the transient post-completion capstone holds before the
 * provider auto-clears it (#1566). Long enough to read the win + let a late
 * encouragement (a mentor round-trip after completion) land and show; short
 * enough that it doesn't linger into the next objective. The reducer also
 * clears it eagerly when the next objective's first entry arrives.
 */
const CAPSTONE_HOLD_MS = 12_000

/**
 * Source of command events the provider matches objectives against.
 * The platform (lab) adapts its terminal `command` frames to this; a
 * test fixture pushes synthetic events.
 */
export interface CommandSource {
  /** Subscribe to command events; returns an unsubscribe handle. */
  onCommand(handler: (event: CommandEvent) => void): Unsubscribe
}

/**
 * One mentor disposition relayed to the dock (#1566). The platform (lab)
 * maps its `mentor` terminal frame onto this verbatim.
 *
 * @property objectiveId - the objective the disposition was judged against.
 * @property disposition - what the mentor did; the reducer maps it (`held`
 *   → standing-by, `nudge` / `encouragement` → transcript entry, else quiet).
 * @property rung - restraint-ladder rung; present on a `nudge`.
 * @property text - learner-facing line; present on a text-bearing
 *   disposition (`nudge` / `encouragement`). Model output — plain text only.
 */
export interface MentorSignal {
  objectiveId: string
  disposition: MentorDockDisposition
  rung?: number
  text?: string
}

/**
 * Source of live mentor signals (#1148 / #1566). The platform (lab) adapts
 * its terminal `mentor` + `mentor-pending` frames — mentor → lab → SPA — to
 * this; the provider dispatches each as a `mentor.disposition` /
 * `mentor.pending` action. Mirrors {@link CommandSource}'s shape so the
 * adapters read identically at the mount site.
 */
export interface MentorSource {
  /** Subscribe to mentor dispositions; returns an unsubscribe handle. */
  onMentor(handler: (signal: MentorSignal) => void): Unsubscribe
  /** Subscribe to the "AI looking…" pending beat; returns an unsubscribe. */
  onPending(handler: (signal: { objectiveId: string }) => void): Unsubscribe
}

/**
 * Recovery-signal sink (#1566): the provider calls this when a satisfying
 * command lands on an objective whose transcript already holds a struggle
 * entry (a hook or a nudge) — the struggle-then-success trigger. The
 * platform (lab) forwards it to the mentor as a `recovered` frame (gated
 * lab-side on the operator's encouragement setting), which drives the
 * success-after-struggle encouragement.
 *
 * @property objectiveId - the objective the satisfying command completed.
 * @property cmd - the command that satisfied it; names why it worked.
 */
export type RecoverySignal = { objectiveId: string; cmd: string }

/**
 * Source of reassert triggers (#1149 reboot-survival). The platform
 * fires the handler when the machine returns to `running` after a
 * non-running spell (resume, reboot, reconnect) — the browser reducer
 * state survived (the SPA never unmounted), so "progress re-asserted"
 * means every state detector re-probes with the rate limit bypassed;
 * goals the reboot itself satisfied (a new generation active, a
 * service now running) then complete. Mirrors {@link CommandSource}'s
 * shape so the adapters read identically at the mount site.
 */
export interface ReassertSource {
  /** Subscribe to reassert triggers; returns an unsubscribe handle. */
  onReassert(handler: () => void): Unsubscribe
}

/**
 * The authored mentoring payload a {@link ChapterFocus} carries. Derived
 * from the active objective's authored fields (the authored `rationale`
 * passed through, wrong-turn mentor hooks as the misconceptions).
 * Structurally identical to the mentor wire's `context` frame body — the
 * agreement is a platform (lab) concern; course-kit stays
 * wire-agnostic, so this shape lives here rather than being imported
 * from the mentor daemon.
 *
 * @property rationale - what the learner is meant to understand — why
 *   the objective's solution works, not just what it is
 * @property misconceptions - soft, semantic traps the mentor watches
 *   for (the objective's wrong turns), each keyed to its `WrongTurn.id`
 *   so the mentor can resolve a fired wrong turn to its hook (#1566)
 */
export interface ChapterFocusMentor {
  rationale: string
  misconceptions: { wrongTurnId: string; hook: string }[]
}

/**
 * The provider's report of what the learner is actively working on
 * (#1148), fired through {@link ChapterProviderProps.onFocus} so the
 * platform can arm the mentor session's context. Structurally identical
 * to the mentor wire's `context` frame body — the agreement is a
 * platform (lab) concern; course-kit stays wire-agnostic.
 *
 * @property objectiveId - the active objective's id
 * @property description - human statement of what the learner is
 *   trying to accomplish, fed to the mentor's model verbatim
 * @property mentor - the authored mentoring payload for the objective
 */
export interface ChapterFocus {
  objectiveId: string
  description: string
  mentor: ChapterFocusMentor
}

/**
 * Reader-funnel transitions the provider observes as it drives a chapter.
 * Emitted through {@link ChapterProviderProps.onFunnel} so the platform
 * can wire them to its analytics sink — course-kit stays analytics-
 * agnostic (no `track()` dependency here; lab maps these to its umami
 * events). Each event fires at most once per chapter mount.
 *
 * Umbrella #1357 retired the per-chapter quiz, so the `quiz-*` beats are
 * gone — the Part-boundary assessment surface owns its own analytics.
 *
 * @property type - discriminator:
 *   `first-command` (the learner ran their first command),
 *   `objective-rethink` (an active objective entered `rethink`),
 *   `rethink-recovered` (a command satisfied an objective that was in
 *   `rethink` — the recovery), `chapter-complete` (the last objective
 *   completed).
 */
export type ReaderFunnelEvent =
  | { type: 'first-command' }
  | { type: 'objective-rethink'; objectiveId: string; wrongTurnId: string }
  | { type: 'rethink-recovered'; objectiveId: string }
  | { type: 'chapter-complete'; chapterId: string }

const StateCtx = createContext<ChapterState | null>(null)
const DispatchCtx = createContext<Dispatch<ChapterAction> | null>(null)

export interface ChapterProviderProps {
  /**
   * Per-chapter spec — built by `ChapterPage` from frontmatter + the
   * registered `<Objective/>` JSX nodes.
   */
  spec: ChapterSpec
  /**
   * Command-event source. The provider subscribes for the chapter's
   * lifetime and unsubscribes on unmount.
   */
  commands: CommandSource
  /**
   * Optional live-mentor source (#1148 / #1566). When present, the provider
   * subscribes for the chapter's lifetime and dispatches each disposition /
   * pending beat as a `mentor.disposition` / `mentor.pending` action;
   * {@link useMentor} exposes the resulting per-objective transcript +
   * capstone + pending state to the MentorDock. Absent = the platform runs
   * mentor-less and the dock only ever shows authored mentor hooks.
   */
  mentor?: MentorSource
  /**
   * Optional recovery-signal sink (#1566). Fired when a satisfying command
   * lands on an objective whose transcript already holds a struggle entry —
   * the struggle-then-success trigger. The platform (lab) forwards it to
   * the mentor (gated on the operator's encouragement setting) so it can
   * generate the success-after-struggle encouragement. Read through a ref
   * internally so an inline closure doesn't churn the command subscription.
   */
  onRecovered?: (signal: RecoverySignal) => void
  /**
   * Optional probe transport for `{via:'state'}` detectors (#1149).
   * When present, the provider runs a {@link StateGoalScheduler} over
   * it: every command event (and every reassert) re-evaluates the
   * active objective's state checks, rate-limited per check. Absent =
   * state detectors never evaluate (they also never match the command
   * stream), so a state-only objective stays open — platforms without a
   * probe path degrade loudly in authoring lint, silently at runtime.
   */
  probes?: ProbeSource
  /**
   * Optional reassert trigger source (#1149 reboot-survival). When
   * present (and `probes` is too), each trigger re-evaluates ALL
   * currently-relevant state checks with the rate limit bypassed. The
   * platform fires it when the machine transitions back into
   * `running` (resume / reboot / reconnect).
   */
  reassert?: ReassertSource
  /**
   * Minimum gap (ms) between two probe evaluations of the same check
   * (#1149). Defaults to 2000; tests lower it to keep specs fast.
   */
  probeIntervalMs?: number
  /**
   * Gap (ms) between the scheduler's self-driven re-evaluations of
   * still-unsatisfied checks (the retry tick). Covers the one-shot
   * trigger gap: a state-only beat whose single completion event races
   * a transiently failing probe would otherwise strand the learner
   * with nothing left to type. Defaults to 10 000; `0` disables.
   */
  probeRetryMs?: number
  /**
   * Optional focus reporter (#1148). Fired whenever the ACTIVE
   * objective changes as the learner advances. The platform (lab)
   * forwards it upstream so the mentor session's context tracks the
   * learner. Read through a ref internally so an inline closure doesn't
   * re-fire the focus effect.
   *
   * Objective focuses derive their mentor-context from authored fields
   * (the authored `rationale` passed through, wrong-turn mentor hooks as
   * the misconceptions).
   */
  onFocus?: (focus: ChapterFocus) => void
  /**
   * Optional analytics sink for {@link ReaderFunnelEvent}s. Called as the
   * provider observes funnel transitions; the platform maps them to its
   * own `track()` vocabulary. Read through a ref internally so passing an
   * inline closure doesn't churn the command subscription.
   */
  onFunnel?: (event: ReaderFunnelEvent) => void
  /**
   * Optional per-objective completion sink (#1493). Fired once for each
   * objective the learner completes — command match, state probe, or a
   * manual "I did it" — so the platform can persist completion and restore
   * the chapter's checkmarks after a VM resume or a reload. Absent =
   * completions stay client-only (the pre-#1493 behaviour). Read through a
   * ref internally so an inline closure doesn't churn effects.
   */
  onObjectiveComplete?: (objectiveId: string) => void
  /**
   * Optional hydration seed (#1493) — objective ids the learner has already
   * completed in this chapter, read from the chapter response. The provider
   * seeds the reducer with them on mount and re-seeds on chapter change, so
   * a VM resume / reload restores the checkmarks. Read through a ref
   * internally so the seed's array identity doesn't churn the spec-change
   * effect.
   */
  completedObjectiveIds?: readonly string[]
  children: ReactNode
}

/**
 * Mount around chapter content. Initialises the reducer with the spec
 * and subscribes to the command-event source; exposes state + dispatch
 * via React Context.
 */
export function ChapterProvider(props: ChapterProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(chapterReducer, props.spec, spec =>
    initialState(spec, props.completedObjectiveIds),
  )

  // Track latest state in a ref so the long-lived command subscription
  // reads `currentObjectiveIndex` without re-subscribing per dispatch.
  const stateRef = useRef(state)
  useEffect(() => {
    stateRef.current = state
  }, [state])

  // Latest `onFunnel` in a ref so an inline closure doesn't churn the
  // command subscription below.
  const onFunnelRef = useRef(props.onFunnel)
  useEffect(() => {
    onFunnelRef.current = props.onFunnel
  }, [props.onFunnel])

  // Latest `onFocus` in a ref (#1148) — same rationale as `onFunnel`:
  // an inline closure must not re-fire the focus effect below.
  const onFocusRef = useRef(props.onFocus)
  useEffect(() => {
    onFocusRef.current = props.onFocus
  }, [props.onFocus])

  // Latest `onRecovered` in a ref (#1566) — same rationale: an inline
  // closure must not churn the command subscription that fires it.
  const onRecoveredRef = useRef(props.onRecovered)
  useEffect(() => {
    onRecoveredRef.current = props.onRecovered
  }, [props.onRecovered])

  // Latest `onObjectiveComplete` in a ref (#1493) — same rationale as the
  // callbacks above: an inline closure must not churn the effect below.
  const onObjectiveCompleteRef = useRef(props.onObjectiveComplete)
  useEffect(() => {
    onObjectiveCompleteRef.current = props.onObjectiveComplete
  }, [props.onObjectiveComplete])

  // Latest hydration seed in a ref (#1493) — the spec-change effect reads it
  // when re-seeding a new chapter without depending on the array's identity
  // (a fresh array each render would churn the effect).
  const seededCompletedRef = useRef(props.completedObjectiveIds)
  useEffect(() => {
    seededCompletedRef.current = props.completedObjectiveIds
  }, [props.completedObjectiveIds])

  // Per-chapter funnel latches — each event fires at most once per mount.
  const firstCommandFiredRef = useRef(false)
  const completeFiredRef = useRef(false)
  // The last focus target reported through `onFocus` (#1148) — each
  // objectiveId fires at most once until the target changes.
  const lastFocusIdRef = useRef<string | null>(null)
  // Objective ids already reported through `onObjectiveComplete` (#1493),
  // so each completion drives the platform persist exactly once per mount.
  // Seeded from the initial completed set so a hydrated (already-persisted)
  // objective never re-reports.
  const reportedCompleteRef = useRef<Set<string>>(
    new Set(state.completedObjectiveIds),
  )

  // The live state-probe scheduler, held in a ref so the active-command
  // effect below can re-evaluate it on a pointer advance (the command
  // subscription reads pre-dispatch state — see that effect).
  const schedulerRef = useRef<StateGoalScheduler | null>(null)

  // Reset progress + funnel latches when navigating to a different chapter.
  useEffect(() => {
    firstCommandFiredRef.current = false
    completeFiredRef.current = false
    lastFocusIdRef.current = null
    // #1493: re-seed the reported set + the reducer with the new chapter's
    // hydrated completions so restored objectives don't re-POST.
    reportedCompleteRef.current = new Set(seededCompletedRef.current ?? [])
    dispatch({
      type: 'spec.replaced',
      spec: props.spec,
      completed: seededCompletedRef.current,
    })
  }, [props.spec])

  // Match each command event against the active objective.
  useEffect(() => {
    const unsub = props.commands.onCommand(event => {
      if (!firstCommandFiredRef.current) {
        firstCommandFiredRef.current = true
        onFunnelRef.current?.({ type: 'first-command' })
      }
      // Count every command for the mentor dock's "watching · N commands"
      // (#939) — before the chapter-complete early-return below, so the
      // tally keeps climbing even after the last objective is cleared.
      dispatch({ type: 'command.observed' })
      const cur = stateRef.current
      const active = cur.spec.objectives[cur.currentObjectiveIndex]
      if (!active) return
      const result = scanObjective(active, cur.currentCommandIndex, event)
      if (result.matchedIndex !== null) {
        // A *satisfying* command on an objective whose transcript already
        // holds a struggle entry (a hook or a nudge) is the recovery — fire
        // before the dispatch moves the transcript to the capstone (#1566).
        // Both the analytics beat and the mentor recovery signal ride the same
        // gate. Gated on `result.satisfied`: a partial match on a multi-command
        // objective (`objective.progressed`, satisfied === false) is not a
        // recovery — firing there would mark capture RECOVERED and spend an
        // encouragement before the objective is actually complete.
        const struggled =
          result.satisfied &&
          cur.transcript.length > 0 &&
          cur.transcript[0]!.objectiveId === active.id
        if (struggled) {
          onFunnelRef.current?.({
            type: 'rethink-recovered',
            objectiveId: active.id,
          })
          onRecoveredRef.current?.({ objectiveId: active.id, cmd: event.cmd })
        }
        dispatch(
          result.satisfied
            ? {
                type: 'objective.completed',
                id: active.id,
                commandIndex: result.matchedIndex,
              }
            : {
                type: 'objective.progressed',
                id: active.id,
                commandIndex: result.matchedIndex,
              },
        )
      } else if (result.wrongTurn) {
        // Only the FIRST wrong turn on an objective is a funnel event; a
        // repeat (a hook already in the transcript for this objective) isn't.
        const alreadyRethinking =
          cur.transcript.length > 0 &&
          cur.transcript[0]!.objectiveId === active.id &&
          cur.transcript.some(e => e.kind === 'hook')
        if (!alreadyRethinking) {
          onFunnelRef.current?.({
            type: 'objective-rethink',
            objectiveId: active.id,
            wrongTurnId: result.wrongTurn.id,
          })
        }
        dispatch({
          type: 'objective.wrongTurn',
          objectiveId: active.id,
          wrongTurnId: result.wrongTurn.id,
          mentorHook: result.wrongTurn.mentorHook,
        })
      }
    })
    return unsub
  }, [props.commands, props.spec])

  // Report each newly-completed objective to the platform (#1493) so it
  // can persist completion for resume. Watching the reducer's completed
  // set catches every completion path — command match, state probe, and
  // manual — in one place, firing each id exactly once per mount.
  useEffect(() => {
    for (const id of state.completedObjectiveIds) {
      if (reportedCompleteRef.current.has(id)) continue
      reportedCompleteRef.current.add(id)
      onObjectiveCompleteRef.current?.(id)
    }
  }, [state.completedObjectiveIds])

  // Live mentor signals (#1148 / #1566) — each disposition dispatches
  // `mentor.disposition` and each pending beat `mentor.pending`; the reducer
  // folds them into the per-objective transcript / capstone / pending state
  // for `useMentor()` / the MentorDock.
  useEffect(() => {
    const source = props.mentor
    if (!source) return
    const unsubs = [
      source.onMentor(signal => {
        dispatch({
          type: 'mentor.disposition',
          objectiveId: signal.objectiveId,
          disposition: signal.disposition,
          ...(signal.rung !== undefined && { rung: signal.rung }),
          ...(signal.text !== undefined && { text: signal.text }),
        })
      }),
      source.onPending(signal => {
        dispatch({ type: 'mentor.pending', objectiveId: signal.objectiveId })
      }),
    ]
    return () => {
      for (const unsub of unsubs) unsub()
    }
  }, [props.mentor])

  // Capstone hold timer (#1566): once a completed objective's transcript
  // moves into the transient capstone, hold it briefly so a late
  // encouragement can land + be read, then dispatch `capstone.cleared`. Keyed
  // on the capstone's objectiveId so a new capstone restarts the timer; the
  // reducer also clears it eagerly when the next objective's first entry
  // arrives.
  useEffect(() => {
    if (!state.capstone) return
    const timer = setTimeout(
      () => dispatch({ type: 'capstone.cleared' }),
      CAPSTONE_HOLD_MS,
    )
    return () => clearTimeout(timer)
  }, [state.capstone?.objectiveId])

  // active objective's scan-window `{via:'state'}` checks on every
  // command event, and re-evaluates everything (rate limit bypassed) on
  // a reassert trigger — the reboot-survival path. A satisfied probe
  // dispatches the SAME progression actions the scanner path uses: it
  // resolves against the active objective's command window
  // (`scanObjectiveCheck`), progressing or completing exactly as a
  // matching command event would. Reads live state through `stateRef`
  // (same pattern as the command effects above) and is torn down — with
  // late settles ignored — on unmount / spec change.
  useEffect(() => {
    const probes = props.probes
    if (!probes) return
    const scheduler = new StateGoalScheduler(
      {
        ...(props.probeIntervalMs !== undefined && {
          minIntervalMs: props.probeIntervalMs,
        }),
        ...(props.probeRetryMs !== undefined && {
          retryIntervalMs: props.probeRetryMs,
        }),
      },
      {
        probes,
        onSatisfied: check => {
          const cur = stateRef.current
          const active = cur.spec.objectives[cur.currentObjectiveIndex]
          if (active) {
            const result = scanObjectiveCheck(
              active,
              cur.currentCommandIndex,
              check,
            )
            if (result.matchedIndex !== null) {
              dispatch(
                result.satisfied
                  ? {
                      type: 'objective.completed',
                      id: active.id,
                      commandIndex: result.matchedIndex,
                    }
                  : {
                      type: 'objective.progressed',
                      id: active.id,
                      commandIndex: result.matchedIndex,
                    },
              )
            }
          }
        },
      },
    )
    schedulerRef.current = scheduler
    const unsubs: Unsubscribe[] = [
      props.commands.onCommand(() => {
        scheduler.evaluate(StateGoalScheduler.collectChecks(stateRef.current))
      }),
    ]
    if (props.reassert) {
      unsubs.push(
        props.reassert.onReassert(() => {
          scheduler.evaluate(
            StateGoalScheduler.collectChecks(stateRef.current),
            { force: true },
          )
        }),
      )
    }
    return () => {
      for (const unsub of unsubs) unsub()
      scheduler.dispose()
      schedulerRef.current = null
    }
  }, [
    props.commands,
    props.probes,
    props.reassert,
    props.probeIntervalMs,
    props.probeRetryMs,
    props.spec,
  ])

  // Re-schedule state probes when the ACTIVE command changes. The command
  // subscription in the effect above reads `stateRef` *before* its own
  // dispatch advances the pointer, so a shell command that advances into a
  // state-checked command — a challenge's required visible query followed by
  // its background proof — would leave that command's check unscheduled until
  // the next command event, stranding the learner. Firing on the post-dispatch
  // pointer closes the gap; `collectChecks` returns [] for a shell-only window,
  // so an advance that reaches no state check schedules nothing.
  useEffect(() => {
    schedulerRef.current?.evaluate(
      StateGoalScheduler.collectChecks(stateRef.current),
    )
  }, [state.currentObjectiveIndex, state.currentCommandIndex])

  // Focus derivation (#1148): the active objective while any remains.
  // Objectives carry a mentor-context built from their authored fields —
  // `goal` is the description, the authored `rationale` is passed through
  // as the rationale (#1457: no longer synthesized from `goal`, which
  // made it byte-identical to `description`), and wrong-turn mentor hooks
  // are the misconceptions.
  const focusTarget = useMemo<ChapterFocus | null>(() => {
    const active = state.spec.objectives[state.currentObjectiveIndex]
    if (!active) return null
    return {
      objectiveId: active.id,
      description: active.goal,
      mentor: {
        rationale: active.rationale,
        misconceptions: active.wrongTurns.map(w => ({
          wrongTurnId: w.id,
          hook: w.mentorHook,
        })),
      },
    }
  }, [state])

  // Report the focus upward once per target change so the platform can
  // arm the mentor's context. Keyed on the target's id — re-rendering
  // while the same objective stays active doesn't re-fire.
  useEffect(() => {
    if (!focusTarget) return
    if (lastFocusIdRef.current === focusTarget.objectiveId) return
    lastFocusIdRef.current = focusTarget.objectiveId
    onFocusRef.current?.(focusTarget)
  }, [focusTarget])

  // Chapter-complete (#629): fires once the chapter reaches the `complete`
  // phase — every objective done (umbrella #1357: completion is
  // objectives-only, the knowledge-check gate moved to the Part boundary).
  // Fired once per mount; the latch resets on `props.spec` change above.
  useEffect(() => {
    if (chapterPhase(state) !== 'complete') return
    if (completeFiredRef.current) return
    completeFiredRef.current = true
    onFunnelRef.current?.({
      type: 'chapter-complete',
      chapterId: state.spec.chapterId,
    })
  }, [state])

  return (
    <StateCtx.Provider value={state}>
      <DispatchCtx.Provider value={dispatch}>
        {props.children}
      </DispatchCtx.Provider>
    </StateCtx.Provider>
  )
}

/**
 * Read the current chapter state. Throws outside `<ChapterProvider/>`.
 */
export function useChapter(): ChapterState {
  const state = useContext(StateCtx)
  if (!state) {
    throw new Error('useChapter must be used inside <ChapterProvider/>')
  }
  return state
}

/**
 * Get the chapter dispatch function. Throws outside
 * `<ChapterProvider/>`. Used by `<Objective/>` for `manual`-mode
 * "I did it" buttons.
 */
export function useChapterDispatch(): Dispatch<ChapterAction> {
  const d = useContext(DispatchCtx)
  if (!d) {
    throw new Error('useChapterDispatch must be used inside <ChapterProvider/>')
  }
  return d
}

/**
 * View model for the mentor dock (#939, transcript + capstone per #1566).
 *
 * @property commandCount - total commands run this chapter, for the
 *   "watching · N commands" state.
 * @property transcript - the active objective's mentor transcript,
 *   oldest→newest (authored hooks + AI nudges/encouragement). The dock
 *   renders each by `kind` — `hook` as trusted HTML, `nudge`/`encouragement`
 *   as plain text (XSS discipline). Empty when the active objective has no
 *   history yet.
 * @property capstone - the transient post-completion slot, or `null`; the
 *   dock shows its entries (the success-after-struggle capstone) briefly
 *   after an objective with a struggle history completes.
 * @property pending - the dock's honest-pending state (`standing-by` /
 *   `looking` / `quiet`).
 */
export interface MentorView {
  commandCount: number
  transcript: TranscriptEntry[]
  capstone: CapstoneState | null
  pending: MentorPending
}

/**
 * Read the mentor dock's view model. Throws outside `<ChapterProvider/>`.
 */
export function useMentor(): MentorView {
  const state = useChapter()
  return {
    commandCount: state.commandCount,
    transcript: state.transcript,
    capstone: state.capstone,
    pending: state.pending,
  }
}

/**
 * Rendering status for an objective card — the Build Spec §5 canonical
 * vocabulary (`locked · active · rethink · done`). The reducer tracks
 * completion as the `objective.completed` *action* and the
 * `completedObjectiveIds` set; this is the *display* status derived for
 * one card. `done` ← completed, `locked` ← not-yet-reachable.
 */
export type ObjectiveStatus = 'locked' | 'active' | 'rethink' | 'done'

/**
 * View model for one `<Objective/>` card.
 *
 * @property status - locked / active / rethink / done
 * @property position - 1-indexed position in `spec.objectives`
 * @property positionLabel - "01", "02", … for the card label
 * @property objective - the objective spec
 * @property commandIndex - index of the first not-yet-matched command
 *   while the card is active; 0 when locked, `commands.length` when done
 * @property satisfiedCommands - indices of `objective.commands` that
 *   actually matched, so the card can check off run commands (and show
 *   which optional ones were skipped on a done card)
 * @property mentorHook - the rethink mentoring line, set only when
 *   `status === 'rethink'`
 */
export interface ObjectiveView {
  status: ObjectiveStatus
  position: number
  positionLabel: string
  objective: Objective
  commandIndex: number
  satisfiedCommands: ReadonlySet<number>
  mentorHook?: string
}

/**
 * Resolve the rendering state for an objective id. Returns `null` if no
 * objective with that id exists (lets `<Objective/>` warn-and-skip on
 * author typos rather than crashing the page).
 *
 * @param id - the objective id from the `<Objective id="…">` JSX prop
 * @returns the view model, or `null`
 */
export function useObjective(id: string): ObjectiveView | null {
  const state = useChapter()
  const idx = state.spec.objectives.findIndex(o => o.id === id)
  if (idx === -1) return null
  const objective = state.spec.objectives[idx]!
  // #1566: an objective is displayed as `rethink` when its transcript holds
  // an authored hook (a wrong turn fired). The transcript is scoped to the
  // active objective, so a hook present ⇒ this active objective is the one
  // rethinking; the latest hook's html is the card's advisory line.
  const latestHook = [...state.transcript]
    .reverse()
    .find(e => e.kind === 'hook' && e.objectiveId === id)
  const isRethink = latestHook !== undefined
  let status: ObjectiveStatus
  if (state.completedObjectiveIds.has(id)) status = 'done'
  else if (state.currentObjectiveIndex === idx)
    status = isRethink ? 'rethink' : 'active'
  else status = 'locked'
  const isActive = state.currentObjectiveIndex === idx
  return {
    status,
    position: idx + 1,
    positionLabel: String(idx + 1).padStart(2, '0'),
    objective,
    commandIndex:
      status === 'done'
        ? objective.commands.length
        : isActive
          ? state.currentCommandIndex
          : 0,
    satisfiedCommands: state.satisfiedCommands.get(id) ?? new Set(),
    mentorHook:
      isRethink && latestHook.kind === 'hook' ? latestHook.html : undefined,
  }
}

/**
 * Read the chapter's current {@link ChapterPhase} — the top-level
 * `objectives → complete` state (umbrella #1357: the quiz phase is retired,
 * so completion is objectives-only). Throws outside `<ChapterProvider/>`.
 *
 * @returns the derived chapter phase
 */
export function useChapterPhase(): ChapterPhase {
  return chapterPhase(useChapter())
}
